import type { SpellDefinition } from '@shefira/shared';
import type Phaser from 'phaser';

import { preference } from '../settings/preferences.js';
import type { CharacterView } from './character-view.js';
import { ScreenShake, type ShakeKind, type ShakeOffset } from './screen-shake.js';

/**
 * The weight of the big moments (ADR 0049): the jolt of the screen, the hit
 * stop, the flash of a level. Presentation only, decided from what the
 * server said; the scene asks and draws. Shakes and flashes follow the
 * player's switch, the hit stop is too short to trouble anyone.
 */

/** How long a critical blow holds the striker and the struck on their frame. */
export const HIT_STOP_MS = 70;
/** A blow that takes this share of the player's health or more jolts the screen. */
export const HEAVY_BLOW_SHARE = 0.2;
/** A crit's number: bigger, in gold, and it lingers. */
export const CRIT_COLOR = 0xffc82e;
export const CRIT_SCALE = 2;

/**
 * How the caster's screen takes a spell: the titan's wrath shakes the ground
 * hard, a spell that strikes around the caster or leaps is felt, the rest
 * not at all.
 */
export function spellShake(spell: SpellDefinition): ShakeKind | null {
  if (spell.fx === 'titan') return 'quake';
  if (spell.targeting === 'area' || spell.effects.some((effect) => effect.kind === 'leap'))
    return 'slam';
  return null;
}

export function isHeavyBlow(amount: number, maxHp: number): boolean {
  return maxHp > 0 && amount >= maxHp * HEAVY_BLOW_SHARE;
}

export class Juice {
  private readonly shake = new ScreenShake();

  constructor(private readonly scene: Phaser.Scene) {}

  /** The camera's offset this frame, whole pixels; none when the player turned shakes off. */
  offset(): ShakeOffset {
    if (!preference('screenShake')) return { x: 0, y: 0 };
    return this.shake.offset(this.scene.time.now);
  }

  jolt(kind: ShakeKind): void {
    if (!preference('screenShake')) return;
    this.shake.kick(kind, this.scene.time.now);
    // The titan's blow lands with a flash of heat as well.
    if (kind === 'quake') this.scene.cameras.main.flash(160, 255, 214, 140);
  }

  /** Striker and struck hold their frame for a beat. */
  hitStop(views: readonly (CharacterView | undefined)[]): void {
    for (const view of views) view?.freeze(HIT_STOP_MS);
  }

  /** A warm flash over the whole screen and a light jolt, for the player's own new level. */
  levelUp(): void {
    this.jolt('level');
    if (preference('screenShake')) this.scene.cameras.main.flash(320, 255, 236, 170);
  }
}
